import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from './store/store';
import { authCheckComplete, restoreSession } from './store/slices/authSlice';
import type { User } from './store/slices/authSlice';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { GitHubConfiguredRoute } from './components/auth/GitHubConfiguredRoute';
import { DashboardLayout } from './components/layouts/DashboardLayout';
import { AuthLayout } from './components/layouts/AuthLayout';
import { LoginPage } from './pages/auth/LoginPage';
import { SignUpPage } from './pages/auth/SignUpPage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { AuthCallbackPage } from './pages/auth/AuthCallbackPage';
import { VerifyEmailPage } from './pages/auth/VerifyEmailPage';
import { GitHubInstalledPage } from './pages/auth/GitHubInstalledPage';
import { GitHubInstallPage } from './pages/onboarding/GitHubInstallPage';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { RepositoriesPage } from './pages/repositories/RepositoriesPage';
import { ImpactSimulatorPage } from './pages/ImpactSimulator/ImpactSimulatorPage';
import { ProfilePage } from './pages/profile/ProfilePage';
import { ScanLayout } from './pages/scans/ScanLayout';
import PoliciesPage from './pages/policies/PoliciesPage';
import RepoPoliciesPage from './pages/policies/RepoPoliciesPage';
import { LoadingSpinner } from './components/common/LoadingSpinner';
import AdminPage from './pages/admin/AdminPage';
import AdminLanguageAnalysisPage from './pages/admin/AdminLanguageAnalysisPage';

const App: React.FC = () => {
  const dispatch = useDispatch();
  const { isLoading, isAuthenticated } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    try {
      const token = localStorage.getItem('accessToken');
      const storedUser = localStorage.getItem('user');
      if (token && storedUser) {
        const user: User = JSON.parse(storedUser);
        dispatch(restoreSession({ user, accessToken: token }));
      } else {
        dispatch(authCheckComplete());
      }
    } catch {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
      dispatch(authCheckComplete());
    }
  }, [dispatch]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Routes>
      {/* Auth pages — wrapped in AuthLayout card */}
      <Route element={<AuthLayout />}>
        <Route path="/auth/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignUpPage />} />
        <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
      </Route>

      {/* Full-screen auth callbacks */}
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/auth/github-installed" element={<GitHubInstalledPage />} />

      {/* Admin — only accessible to admin_email, redirects others to dashboard */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/languages"
        element={
          <ProtectedRoute>
            <AdminLanguageAnalysisPage />
          </ProtectedRoute>
        }
      />

      {/* Onboarding — authenticated but GitHub App not yet installed */}
      <Route
        path="/onboarding/github-install"
        element={
          <ProtectedRoute>
            <GitHubInstallPage />
          </ProtectedRoute>
        }
      />

      {/* Protected — requires auth + GitHub App installed */}
      <Route
        element={
          <ProtectedRoute>
            <GitHubConfiguredRoute>
              <DashboardLayout />
            </GitHubConfiguredRoute>
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/repositories" element={<RepositoriesPage />} />
        <Route path="/remediation-planner" element={<ImpactSimulatorPage />} />
        <Route path="/impact-simulator" element={<ImpactSimulatorPage />} />
        <Route path="/policies" element={<PoliciesPage />} />
        <Route path="/repos/:repoId/policies" element={<RepoPoliciesPage />} />
        <Route path="/profile" element={<ProfilePage />} />

        {/* Scan pages — nested under ScanLayout (persistent header + sub-nav + cached tab host) */}
        <Route path="/scans/:scanId/*" element={<ScanLayout />} />
        <Route path="/scans/:scanId" element={<ScanLayout />} />
      </Route>

      {/* Default redirect */}
      <Route
        path="/"
        element={<Navigate to={isAuthenticated ? '/dashboard' : '/auth/login'} replace />}
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default App;
